import { useRef, useState } from 'react'
import type { DragEvent } from 'react'

/** Native drag state is separate from the controlled column order. */
export function useColumnReorder(onMove: (source: string, target: string) => void) {
  const source = useRef<string | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)
  const [target, setTarget] = useState<string | null>(null)
  const clear = () => {
    source.current = null
    setDragging(null)
    setTarget(null)
  }
  return {
    clear,
    stateOf: (id: string): 'source' | 'target' | 'idle' => dragging === id ? 'source' : target === id ? 'target' : 'idle',
    handleProps: (id: string) => ({
      onDragStart: (event: DragEvent<HTMLElement>) => {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', id)
        const preview = event.currentTarget.closest<HTMLElement>('[data-column-id]')
        if (preview) event.dataTransfer.setDragImage?.(preview, 16, 16)
        source.current = id
        setDragging(id)
      },
      onDragEnd: clear,
    }),
    targetProps: (id: string) => ({
      onDragOver: (event: DragEvent<HTMLElement>) => {
        if (source.current === null || source.current === id) return
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
        setTarget(id)
      },
      onDragLeave: (event: DragEvent<HTMLElement>) => {
        if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return
        setTarget((current) => current === id ? null : current)
      },
      onDrop: (event: DragEvent<HTMLElement>) => {
        if (source.current === null) return
        event.preventDefault()
        onMove(source.current, id)
        clear()
      },
    }),
  }
}
